import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Badge,
  Card,
  CardBody,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
  useColorModeValue,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Alert,
  AlertIcon,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Switch
} from '@chakra-ui/react';
import { 
  FiPlus, 
  FiSearch, 
  FiEdit, 
  FiTrash2, 
  FiEye, 
  FiMoreVertical,
  FiPackage,
  FiUsers
} from 'react-icons/fi';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const Services = () => {
  const { t, currentLanguage } = useLanguage();
  const toast = useToast();
  const queryClient = useQueryClient();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [pricingMethodFilter, setPricingMethodFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [sortBy] = useState('name');
  const [sortOrder] = useState('asc');
  
  
  const { isOpen: isNewOpen, onOpen: onNewOpen, onClose: onNewClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  
  const [selectedService, setSelectedService] = useState(null);
  const [newService, setNewService] = useState({
    name: '',
    name_en: '',
    name_de: '',
    description: '',
    description_en: '',
    description_de: '',
    sku: '',
    category: '',
    service_class: '',
    service_subclass: '',
    base_price: '',
    currency: '',
    pricing_method: 'per_hour',
    estimated_duration_hours: '',
    estimated_duration_days: '',
    min_quantity: '1',
    max_quantity: '',
    is_active: true,
    requires_materials: false,
    requires_equipment: false,
    requires_skills: '',
    quality_standards: '',
    warranty_period_months: ''
  });

  // Queries
  const { data: servicesData, isLoading, error } = useQuery(
    ['services', searchTerm, categoryFilter, pricingMethodFilter, activeFilter, sortBy, sortOrder],
    async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('q', searchTerm);
      if (categoryFilter) params.append('category', categoryFilter);
      if (pricingMethodFilter) params.append('pricing_method', pricingMethodFilter);
      if (activeFilter !== '') params.append('is_active', activeFilter);
      if (sortBy) params.append('sort_by', sortBy);
      if (sortOrder) params.append('sort_order', sortOrder);
      
      const response = await api.get(`/api/services?${params.toString()}`);
      return response.data;
    }
  );

  const { data: categoriesData } = useQuery(
    ['service-categories'],
    async () => {
      const response = await api.get('/api/services/categories');
      return response.data;
    }
  );

  const { data: classesData } = useQuery(
    ['service-classes'],
    async () => {
      const response = await api.get('/api/services/classes/list');
      return response.data;
    }
  );

  const { data: pricingMethodsData } = useQuery(
    ['service-pricing-methods'],
    async () => {
      const response = await api.get('/api/services/pricing-methods/list');
      return response.data;
    }
  );

  // Mutations
  const createService = useMutation(
    async (serviceData) => {
      const response = await api.post('/api/services', serviceData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['services']);
        toast({ title: t('common.success'), description: t('services.created_success', {defaultValue: 'Service created successfully'}), status: 'success' });
        onNewClose();
        setNewService({
          name: '', name_en: '', name_de: '', description: '', description_en: '', description_de: '',
          sku: '', category: '', service_class: '', service_subclass: '', base_price: '',
          currency: '', pricing_method: 'per_hour', estimated_duration_hours: '',
          estimated_duration_days: '', min_quantity: '1', max_quantity: '', is_active: true,
          requires_materials: false, requires_equipment: false, requires_skills: '',
          quality_standards: '', warranty_period_months: ''
        });
      },
      onError: (error) => {
        toast({ title: t('common.error'), description: error.response?.data?.error || t('services.create_failed', {defaultValue: 'Failed to create service'}), status: 'error' });
      }
    }
  );

  const updateService = useMutation(
    async ({ id, data }) => {
      const response = await api.put(`/api/services/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['services']);
        toast({ title: t('common.success'), description: t('services.updated_success', {defaultValue: 'Service updated successfully'}), status: 'success' });
        onNewClose();
        setSelectedService(null);
      },
      onError: (error) => {
        toast({ title: t('common.error'), description: error.response?.data?.error || t('services.update_failed', {defaultValue: 'Failed to update service'}), status: 'error' });
      }
    }
  );

  const deleteService = useMutation(
    async (id) => {
      const response = await api.delete(`/api/services/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['services']);
        toast({ title: t('common.success'), description: t('services.deleted_success', {defaultValue: 'Service deleted successfully'}), status: 'success' });
      },
      onError: (error) => {
        toast({ title: t('common.error'), description: error.response?.data?.error || t('services.delete_failed', {defaultValue: 'Failed to delete service'}), status: 'error' });
      }
    }
  );


  // Computed values
  const services = servicesData?.data?.services || [];
  const total = servicesData?.data?.total || 0;
  const categories = categoriesData?.data?.categories || [];
  const categoryNames = categories.map((c) => {
    const displayName = currentLanguage === 'de' ? (c.name_de || c.name_en) : (c.name_en || c.name_de);
    return { value: c.name_de || c.name_en, label: displayName };
  }).filter(cat => cat.value);
  const pricingMethods = pricingMethodsData?.data?.pricingMethods || [];

  const filteredServices = useMemo(() => {
    return services.filter(service => {
      const displayName = currentLanguage === 'de' ? (service.name_de || service.name_en || service.name) : (service.name_en || service.name_de || service.name);
      const matchesSearch = !searchTerm || 
        (displayName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.category?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = !categoryFilter || service.category === categoryFilter;
      const matchesPricingMethod = !pricingMethodFilter || service.pricing_method === pricingMethodFilter;
      const matchesActive = activeFilter === '' || service.is_active === (activeFilter === 'true');
      
      return matchesSearch && matchesCategory && matchesPricingMethod && matchesActive;
    });
  }, [services, searchTerm, categoryFilter, pricingMethodFilter, activeFilter]);

  // Handlers
  const handleCreate = () => {
    setSelectedService(null);
    setNewService({
      name: '', name_en: '', name_de: '', description: '', description_en: '', description_de: '',
      sku: '', category: '', service_class: '', service_subclass: '', base_price: '',
      currency: '', pricing_method: 'per_hour', estimated_duration_hours: '',
      estimated_duration_days: '', min_quantity: '1', max_quantity: '', is_active: true,
      requires_materials: false, requires_equipment: false, requires_skills: '',
      quality_standards: '', warranty_period_months: ''
    });
    onNewOpen();
  };

  const handleEdit = (service) => {
    setSelectedService(service);
    setNewService({
      name: service.name || '',
      name_en: service.name_en || '',
      name_de: service.name_de || service.name || '',
      description: service.description || '',
      description_en: service.description_en || '',
      description_de: service.description_de || '',
      sku: service.sku || '',
      category: service.category || '',
      service_class: service.service_class || '',
      service_subclass: service.service_subclass || '',
      base_price: service.base_price || '',
      currency: service.currency || '',
      pricing_method: service.pricing_method || 'per_hour',
      estimated_duration_hours: service.estimated_duration_hours || '',
      estimated_duration_days: service.estimated_duration_days || '',
      min_quantity: service.min_quantity || '1',
      max_quantity: service.max_quantity || '',
      is_active: service.is_active !== undefined ? service.is_active : true,
      requires_materials: service.requires_materials || false,
      requires_equipment: service.requires_equipment || false,
      requires_skills: service.requires_skills || '',
      quality_standards: service.quality_standards || '',
      warranty_period_months: service.warranty_period_months || ''
    });
    onNewOpen();
  };

  const handleView = (service) => {
    setSelectedService(service);
    onViewOpen();
  };

  const handleDelete = (id) => {
    if (window.confirm(t('services.confirm_delete', {defaultValue: 'Are you sure you want to delete this service?'}))) {
      deleteService.mutate(id);
    }
  };


  const handleSave = () => {
    if (!newService.name_de) {
      toast({ title: t('common.error'), description: t('services.name_de_required', {defaultValue: 'German name is required'}), status: 'error' });
      return;
    }

    if (selectedService) {
      updateService.mutate({ id: selectedService.id, data: newService });
    } else {
      createService.mutate(newService);
    }
  };

  const handleInputChange = (field, value) => {
    setNewService(prev => ({ ...prev, [field]: value }));
  };

  const getPricingMethodLabel = (method) => {
    return t(`services.pricing_methods.${method}`, { defaultValue: method });
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'green' : 'red';
  };

  if (error) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          {t('services.load_failed', { message: error.message, defaultValue: `Failed to load services: ${error.message}` })}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <Heading size="lg" mb={6}>{t('services.title', {defaultValue: 'Services Management'})}</Heading>

      {/* Statistics */}
      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6} mb={6}>
        <Stat>
          <StatLabel>{t('services.stats.total', {defaultValue: 'Total Services'})}</StatLabel>
          <StatNumber>{total}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>{t('services.stats.active', {defaultValue: 'Active Services'})}</StatLabel>
          <StatNumber>{services.filter(s => s.is_active).length}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>{t('services.stats.categories', {defaultValue: 'Categories'})}</StatLabel>
          <StatNumber>{categories.length}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>{t('services.stats.pricing_methods', {defaultValue: 'Pricing Methods'})}</StatLabel>
          <StatNumber>{pricingMethods.length}</StatNumber>
        </Stat>
      </SimpleGrid>

      {/* Filters and Actions */}
      <Card bg={bgColor} border="1px" borderColor={borderColor} mb={6}>
        <CardBody>
          <VStack spacing={4}>
            <HStack w="100%" spacing={4} flexWrap="wrap">
              <InputGroup maxW="300px">
                <InputLeftElement>
                  <Icon as={FiSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder={t('common.search') || 'Search services...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              
              <Select
                placeholder={t('services.filter.all_categories', {defaultValue: 'All Categories'})}
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                maxW="200px"
              >
                {categoryNames.map(category => (
                  <option key={category.value} value={category.value}>{category.label}</option>
                ))}
              </Select>
              
              <Select
                placeholder={t('services.filter.all_pricing_methods', {defaultValue: 'All Pricing Methods'})}
                value={pricingMethodFilter}
                onChange={(e) => setPricingMethodFilter(e.target.value)}
                maxW="200px"
              >
                {pricingMethods.map(method => (
                  <option key={method} value={method}>{getPricingMethodLabel(method)}</option>
                ))}
              </Select>
              
              <Select
                placeholder={t('services.filter.all_status', {defaultValue: 'All Status'})}
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
                maxW="150px"
              >
                <option value="">{t('common.all', {defaultValue: 'All'})}</option>
                <option value="true">{t('common.active', {defaultValue: 'Active'})}</option>
                <option value="false">{t('common.inactive', {defaultValue: 'Inactive'})}</option>
              </Select>
              
              <Button
                leftIcon={<FiPlus />}
                colorScheme="blue"
                onClick={handleCreate}
              >
                {t('services.new_button', {defaultValue: 'New Service'})}
              </Button>
              
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Services Table */}
      <Card bg={bgColor} border="1px" borderColor={borderColor}>
        <CardBody>
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>{t('common.name')}</Th>
                  <Th>{t('inventory.sku')}</Th>
                  <Th>{t('common.category')}</Th>
                  <Th>{t('services.pricing_method', {defaultValue: 'Pricing Method'})}</Th>
                  <Th>{t('services.base_price', {defaultValue: 'Base Price'})}</Th>
                  <Th>{t('nav.suppliers')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th>{t('common.actions')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredServices.map((service) => (
                  <Tr key={service.id}>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="medium">{currentLanguage === 'de' ? (service.name_de || service.name_en || service.name) : (service.name_en || service.name_de || service.name)}</Text>
                        {service.description && (
                          <Text fontSize="sm" color="gray.600" noOfLines={2}>
                            {service.description}
                          </Text>
                        )}
                      </VStack>
                    </Td>
                    <Td>
                      {service.sku ? (
                        <Badge colorScheme="blue" variant="outline">{service.sku}</Badge>
                      ) : (
                        <Text color="gray.400">-</Text>
                      )}
                    </Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Text>{service.category || '-'}</Text>
                        {service.service_class && (
                          <Text fontSize="xs" color="gray.500">{service.service_class}</Text>
                        )}
                      </VStack>
                    </Td>
                    <Td>
                      <Badge colorScheme="purple" variant="outline">
                        {getPricingMethodLabel(service.pricing_method)}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontWeight="medium">
                        {service.base_price} {service.currency}
                      </Text>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Icon as={FiUsers} color="blue.500" />
                        <Text>{service.supplier_count || 0}</Text>
                        {service.pricing_count > 0 && (
                          <Badge colorScheme="green" size="sm">
                            {t('services.pricing_count', { count: service.pricing_count, defaultValue: `${service.pricing_count} pricing` })}
                          </Badge>
                        )}
                      </HStack>
                    </Td>
                    <Td>
                      <Badge colorScheme={getStatusColor(service.is_active)}>
                        {service.is_active ? t('common.active', {defaultValue: 'Active'}) : t('common.inactive', {defaultValue: 'Inactive'})}
                      </Badge>
                    </Td>
                    <Td>
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<FiMoreVertical />}
                          variant="ghost"
                          size="sm"
                        />
                        <MenuList>
                          <MenuItem icon={<FiEye />} onClick={() => handleView(service)}>
                            {t('common.view')}
                          </MenuItem>
                          <MenuItem icon={<FiEdit />} onClick={() => handleEdit(service)}>
                            {t('common.edit')}
                          </MenuItem>
                          <MenuDivider />
                          <MenuItem 
                            icon={<FiTrash2 />} 
                            onClick={() => handleDelete(service.id)}
                            color="red.500"
                          >
                            {t('common.delete')}
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
          
          {filteredServices.length === 0 && !isLoading && (
            <Box textAlign="center" py={8}>
              <Icon as={FiPackage} size="48px" color="gray.400" mb={4} />
              <Text color="gray.500">{t('services.no_data', {defaultValue: 'No services found'})}</Text>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* Create/Edit Service Modal */}
      <Modal isOpen={isNewOpen} onClose={onNewClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedService ? t('services.edit_title', {defaultValue: 'Edit Service'}) : t('services.new_title', {defaultValue: 'Create New Service'})}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Tabs>
              <TabList>
                <Tab>{t('services.modal.tab_basic', {defaultValue: 'Basic Info'})}</Tab>
                <Tab>{t('services.modal.tab_pricing', {defaultValue: 'Pricing & Duration'})}</Tab>
                <Tab>{t('services.modal.tab_requirements', {defaultValue: 'Requirements & Standards'})}</Tab>
                {selectedService && <Tab>{t('inventory.sub_descriptions', {defaultValue: 'Sub Descriptions'})}</Tab>}
              </TabList>
              
              <TabPanels>
                {/* Basic Info Tab */}
                <TabPanel>
                  <VStack spacing={4} align="stretch">
                    <HStack spacing={4}>
                      <FormControl isRequired>
                        <FormLabel>{t('services.form.name_de', {defaultValue: 'Name (German)'})}</FormLabel>
                        <Input
                          value={newService.name_de}
                          onChange={(e) => handleInputChange('name_de', e.target.value)}
                          placeholder={t('services.form.name_de_placeholder', {defaultValue: 'Service name in German'})}
                        />
                      </FormControl>
                      
                      <FormControl>
                        <FormLabel>{t('services.form.name_en', {defaultValue: 'Name (English)'})}</FormLabel>
                        <Input
                          value={newService.name_en}
                          onChange={(e) => handleInputChange('name_en', e.target.value)}
                          placeholder={t('services.form.name_en_placeholder', {defaultValue: 'Service name in English'})}
                        />
                      </FormControl>
                    </HStack>

                    <HStack spacing={4}>
                      <FormControl>
                        <FormLabel>{t('inventory.sku')}</FormLabel>
                        <Input
                          value={newService.sku}
                          onChange={(e) => handleInputChange('sku', e.target.value)}
                          placeholder={t('services.form.sku_placeholder', {defaultValue: 'Service SKU'})}
                        />
                      </FormControl>
                    </HStack>
                    
                    <FormControl>
                      <FormLabel>{t('common.description')}</FormLabel>
                      <Textarea
                        value={newService.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        placeholder={t('services.form.description_placeholder', {defaultValue: 'Service description'})}
                        rows={3}
                      />
                    </FormControl>
                    
                    <HStack spacing={4}>
                      <FormControl>
                        <FormLabel>{t('common.category')}</FormLabel>
                        <Select
                          value={newService.category}
                          onChange={(e) => handleInputChange('category', e.target.value)}
                          placeholder={t('services.form.select_category', {defaultValue: 'Select category'})}
                        >
                          {categoryNames.map(category => (
                            <option key={category.value} value={category.value}>{category.label}</option>
                          ))}
                        </Select>
                      </FormControl>
                      
                      {/* Service Class kaldırıldı */}
                    </HStack>
                    
                    <FormControl>
                      <FormLabel>{t('services.form.service_subclass', {defaultValue: 'Service Subclass'})}</FormLabel>
                      <Input
                        value={newService.service_subclass}
                        onChange={(e) => handleInputChange('service_subclass', e.target.value)}
                        placeholder={t('services.form.subclass_placeholder', {defaultValue: 'Service subclass'})}
                      />
                    </FormControl>
                  </VStack>
                </TabPanel>
                
                {/* Pricing & Duration Tab */}
                <TabPanel>
                  <VStack spacing={4} align="stretch">
                    <HStack spacing={4}>
                      <FormControl>
                        <FormLabel>{t('services.base_price')}</FormLabel>
                        <NumberInput
                          value={newService.base_price}
                          onChange={(value) => handleInputChange('base_price', value)}
                          min={0}
                          precision={2}
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                      
                      <FormControl>
                        <FormLabel>{t('common.currency')}</FormLabel>
                        <Select
                          value={newService.currency}
                          onChange={(e) => handleInputChange('currency', e.target.value)}
                        >
                          <option value="EUR">EUR</option>
                          <option value="USD">USD</option>
                        </Select>
                      </FormControl>
                      
                      <FormControl>
                        <FormLabel>{t('services.pricing_method')}</FormLabel>
                        <Select
                          value={newService.pricing_method}
                          onChange={(e) => handleInputChange('pricing_method', e.target.value)}
                        >
                          {pricingMethods.map(method => (
                            <option key={method} value={method}>{getPricingMethodLabel(method)}</option>
                          ))}
                        </Select>
                      </FormControl>
                    </HStack>
                    
                    <HStack spacing={4}>
                      <FormControl>
                        <FormLabel>{t('services.form.duration_hours', {defaultValue: 'Estimated Duration (Hours)'})}</FormLabel>
                        <NumberInput
                          value={newService.estimated_duration_hours}
                          onChange={(value) => handleInputChange('estimated_duration_hours', value)}
                          min={0}
                          precision={2}
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                      
                      <FormControl>
                        <FormLabel>{t('services.form.duration_days', {defaultValue: 'Estimated Duration (Days)'})}</FormLabel>
                        <NumberInput
                          value={newService.estimated_duration_days}
                          onChange={(value) => handleInputChange('estimated_duration_days', value)}
                          min={0}
                          precision={2}
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                    </HStack>
                    
                    <HStack spacing={4}>
                      <FormControl>
                        <FormLabel>{t('services.form.min_quantity', {defaultValue: 'Min Quantity'})}</FormLabel>
                        <NumberInput
                          value={newService.min_quantity}
                          onChange={(value) => handleInputChange('min_quantity', value)}
                          min={0}
                          precision={4}
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                      
                      <FormControl>
                        <FormLabel>{t('services.form.max_quantity', {defaultValue: 'Max Quantity'})}</FormLabel>
                        <NumberInput
                          value={newService.max_quantity}
                          onChange={(value) => handleInputChange('max_quantity', value)}
                          min={0}
                          precision={4}
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                    </HStack>
                  </VStack>
                </TabPanel>
                
                {/* Requirements & Standards Tab */}
                <TabPanel>
                  <VStack spacing={4} align="stretch">
                    <HStack spacing={4}>
                      <FormControl>
                        <FormLabel>{t('services.form.requires_materials', {defaultValue: 'Requires Materials'})}</FormLabel>
                        <Switch
                          isChecked={newService.requires_materials}
                          onChange={(e) => handleInputChange('requires_materials', e.target.checked)}
                        />
                      </FormControl>
                      
                      <FormControl>
                        <FormLabel>{t('services.form.requires_equipment', {defaultValue: 'Requires Equipment'})}</FormLabel>
                        <Switch
                          isChecked={newService.requires_equipment}
                          onChange={(e) => handleInputChange('requires_equipment', e.target.checked)}
                        />
                      </FormControl>
                    </HStack>
                    
                    <FormControl>
                      <FormLabel>{t('services.form.required_skills', {defaultValue: 'Required Skills'})}</FormLabel>
                      <Textarea
                        value={newService.requires_skills}
                        onChange={(e) => handleInputChange('requires_skills', e.target.value)}
                        placeholder={t('services.form.required_skills_placeholder', {defaultValue: 'Required skills and certifications'})}
                        rows={3}
                      />
                    </FormControl>
                    
                    <FormControl>
                      <FormLabel>{t('services.form.quality_standards', {defaultValue: 'Quality Standards'})}</FormLabel>
                      <Textarea
                        value={newService.quality_standards}
                        onChange={(e) => handleInputChange('quality_standards', e.target.value)}
                        placeholder={t('services.form.quality_standards_placeholder', {defaultValue: 'Quality standards and requirements'})}
                        rows={3}
                      />
                    </FormControl>
                    
                    <FormControl>
                      <FormLabel>{t('services.form.warranty', {defaultValue: 'Warranty Period (Months)'})}</FormLabel>
                      <NumberInput
                        value={newService.warranty_period_months}
                        onChange={(value) => handleInputChange('warranty_period_months', value)}
                        min={0}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    
                    <FormControl>
                      <FormLabel>{t('services.form.active_status', {defaultValue: 'Active Status'})}</FormLabel>
                      <Switch
                        isChecked={newService.is_active}
                        onChange={(e) => handleInputChange('is_active', e.target.checked)}
                      />
                    </FormControl>
                  </VStack>
                </TabPanel>
                {selectedService && (
                  <TabPanel>
                    <ServiceSubDesc serviceId={selectedService.id} />
                  </TabPanel>
                )}
              </TabPanels>
            </Tabs>
          </ModalBody>
          
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={onNewClose}>
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSave}
                isLoading={createService.isLoading || updateService.isLoading}
              >
                {selectedService ? t('common.update') : t('common.save')}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* View Service Modal */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('services.view.title', {defaultValue: 'Service Details'})}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedService && (
              <VStack spacing={6} align="stretch">
                {/* Basic Info */}
                <Card>
                  <CardBody>
                    <Heading size="md" mb={4}>{t('services.view.basic_info', {defaultValue: 'Basic Information'})}</Heading>
                    <SimpleGrid columns={2} spacing={4}>
                      <Box>
                        <Text fontWeight="bold">{t('common.name')}:</Text>
                        <Text>{selectedService.name}</Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold">{t('inventory.sku')}:</Text>
                        <Text>{selectedService.sku || '-'}</Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold">{t('common.category')}:</Text>
                        <Text>{selectedService.category || '-'}</Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold">{t('services.form.service_class')}:</Text>
                        <Text>{selectedService.service_class || '-'}</Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold">{t('common.description')}:</Text>
                        <Text>{selectedService.description || '-'}</Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold">{t('common.status')}:</Text>
                        <Badge colorScheme={getStatusColor(selectedService.is_active)}>
                          {selectedService.is_active ? t('common.active') : t('common.inactive')}
                        </Badge>
                      </Box>
                    </SimpleGrid>
                  </CardBody>
                </Card>
                
                {/* Pricing Info */}
                <Card>
                  <CardBody>
                    <Heading size="md" mb={4}>{t('services.view.pricing_duration', {defaultValue: 'Pricing & Duration'})}</Heading>
                    <SimpleGrid columns={2} spacing={4}>
                      <Box>
                        <Text fontWeight="bold">{t('services.base_price')}:</Text>
                        <Text>{selectedService.base_price} {selectedService.currency}</Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold">{t('services.pricing_method')}:</Text>
                        <Text>{getPricingMethodLabel(selectedService.pricing_method)}</Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold">{t('services.form.duration_hours')}:</Text>
                        <Text>{selectedService.estimated_duration_hours || '-'}</Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold">{t('services.form.duration_days')}:</Text>
                        <Text>{selectedService.estimated_duration_days || '-'}</Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold">{t('services.form.min_quantity')}:</Text>
                        <Text>{selectedService.min_quantity}</Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold">{t('services.form.max_quantity')}:</Text>
                        <Text>{selectedService.max_quantity || '-'}</Text>
                      </Box>
                    </SimpleGrid>
                  </CardBody>
                </Card>
                
                {/* Requirements */}
                <Card>
                  <CardBody>
                    <Heading size="md" mb={4}>{t('services.view.requirements_standards', {defaultValue: 'Requirements & Standards'})}</Heading>
                    <SimpleGrid columns={2} spacing={4}>
                      <Box>
                        <Text fontWeight="bold">{t('services.form.requires_materials')}:</Text>
                        <Badge colorScheme={selectedService.requires_materials ? 'green' : 'red'}>
                          {selectedService.requires_materials ? t('common.yes') : t('common.no')}
                        </Badge>
                      </Box>
                      <Box>
                        <Text fontWeight="bold">{t('services.form.requires_equipment')}:</Text>
                        <Badge colorScheme={selectedService.requires_equipment ? 'green' : 'red'}>
                          {selectedService.requires_equipment ? t('common.yes') : t('common.no')}
                        </Badge>
                      </Box>
                      <Box>
                        <Text fontWeight="bold">{t('services.form.required_skills')}:</Text>
                        <Text>{selectedService.requires_skills || '-'}</Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold">{t('services.form.quality_standards')}:</Text>
                        <Text>{selectedService.quality_standards || '-'}</Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold">{t('services.form.warranty')}:</Text>
                        <Text>{selectedService.warranty_period_months ? t('services.view.warranty_months', {count: selectedService.warranty_period_months, defaultValue: `${selectedService.warranty_period_months} months`}) : '-'}</Text>
                      </Box>
                    </SimpleGrid>
                  </CardBody>
                </Card>
              </VStack>
            )}
          </ModalBody>
          
          <ModalFooter>
            <Button variant="ghost" onClick={onViewClose}>
              {t('common.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Box>
  );
};

export default Services;

// Inline component for services sub-descriptions
const ServiceSubDesc = ({ serviceId }) => {
  const { t } = useLanguage();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const toast = useToast();

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/api/meta/sub-descriptions', { params: { item_type: 'service', item_id: serviceId } });
      setList(data?.data?.items || []);
    } catch (_) {
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (serviceId) load(); }, [serviceId]);

  const addOne = async () => {
    try {
      if (!input.trim()) return;
      await api.post('/api/meta/sub-descriptions', { item_type: 'service', item_id: serviceId, sub_description: input.trim() });
      setInput('');
      load();
      toast({ title: t('common.success'), status: 'success' });
    } catch (e) {
      toast({ title: t('common.error'), status: 'error' });
    }
  };

  const removeOne = async (id) => {
    try {
      await api.delete(`/api/meta/sub-descriptions/${id}`);
      load();
    } catch (e) {}
  };

  return (
    <VStack align="stretch" spacing={3}>
      <HStack>
        <Input placeholder={t('common.add') || 'Add sub description'} value={input} onChange={(e)=> setInput(e.target.value)} />
        <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={addOne} isDisabled={!input.trim()}>{t('common.add')}</Button>
      </HStack>
      <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={2}>
        {loading ? (
          <Text fontSize="sm" color="gray.500">{t('common.loading') || 'Loading...'}</Text>
        ) : (
          <VStack align="stretch" spacing={1}>
            {(list || []).map(row => (
              <HStack key={row.id} justify="space-between">
                <Text fontSize="sm">+ {row.sub_description}</Text>
                <IconButton aria-label="delete" size="sm" variant="ghost" colorScheme="red" icon={<FiTrash2 />} onClick={()=> removeOne(row.id)} />
              </HStack>
            ))}
          </VStack>
        )}
      </Box>
    </VStack>
  );
};
